import { useState, useEffect, useCallback, useRef } from 'react';
import mapApi from 'api/map';
import { JOB_COMPLETED, JOB_FAILED } from 'constants/index';

export const useImportJob = () => {
    const [jobs, setJobs] = useState(new Map());
    const intervalRefs = useRef(new Map());

    const startJobPolling = useCallback((jobId, onUpdate, onComplete, onError) => {
        // Clear existing interval if any
        if (intervalRefs.current.has(jobId)) {
            clearInterval(intervalRefs.current.get(jobId));
        }

        const pollJob = async () => {
            try {
                const response = await mapApi.getImportJobStatus(jobId);
                const jobData = response.data;

                setJobs(prev => new Map(prev.set(jobId, jobData)));

                if (onUpdate) {
                    onUpdate(jobData);
                }

                if (jobData.status === JOB_COMPLETED) {
                    clearInterval(intervalRefs.current.get(jobId));
                    intervalRefs.current.delete(jobId);
                    if (onComplete) {
                        onComplete(jobData);
                    }
                } else if (jobData.status === JOB_FAILED) {
                    clearInterval(intervalRefs.current.get(jobId));
                    intervalRefs.current.delete(jobId);
                    if (onError) {
                        onError(jobData);
                    }
                }
            } catch (error) {
                console.error('Error polling job:', error);
                clearInterval(intervalRefs.current.get(jobId));
                intervalRefs.current.delete(jobId);
                if (onError) {
                    onError({ error: error.message });
                }
            }
        };

        // Poll immediately
        pollJob();

        // Set up interval polling every 2 seconds
        const intervalId = setInterval(pollJob, 2000);
        intervalRefs.current.set(jobId, intervalId);

        return () => {
            clearInterval(intervalId);
            intervalRefs.current.delete(jobId);
        };
    }, []);

    const stopJobPolling = useCallback((jobId) => {
        if (intervalRefs.current.has(jobId)) {
            clearInterval(intervalRefs.current.get(jobId));
            intervalRefs.current.delete(jobId);
        }
    }, []);

    const getJob = useCallback((jobId) => {
        return jobs.get(jobId);
    }, [jobs]);

    const clearJob = useCallback((jobId) => {
        stopJobPolling(jobId);
        setJobs(prev => {
            const newMap = new Map(prev);
            newMap.delete(jobId);
            return newMap;
        });
    }, [stopJobPolling]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            intervalRefs.current.forEach((intervalId) => {
                clearInterval(intervalId);
            });
            intervalRefs.current.clear();
        };
    }, []);

    return {
        jobs,
        startJobPolling,
        stopJobPolling,
        getJob,
        clearJob
    };
};

export default useImportJob;
